import {useState} from 'react'
import {useNavigate} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Input} from '@/components/ui/input'
import {SearchComboboxShell} from '@/components/search-combobox-shell'
import {NuevaEvaluacionDialog} from '@/components/bateria/nueva-evaluacion-dialog'
import {useBusinesses, type Business} from '@/hooks/empresa/organizacion/use-businesses'
import {useDebouncedValue} from '@/hooks/common/use-debounced-value'
import {tope} from '@/lib/campos-texto'

function Seccion({children}: {children: string}) {
  return (
    <li className="px-2 pt-2 pb-1 font-mono text-[10px] font-semibold tracking-[0.12em] text-muted-foreground uppercase">
      {children}
    </li>
  )
}

/** Única puerta para empezar una batería (UX-15): una empresa de Kairos va a `NuevaEvaluacionDialog`
 * y una que no está, al alta ad-hoc por Excel. */
export function NuevaBateriaDoor() {
  const {t} = useTranslation()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const search = useDebouncedValue(query, 200)
  const [empresa, setEmpresa] = useState<Business | null>(null)
  const {data} = useBusinesses({page: 1, search, sortBy: 'name', sortDir: 'asc'}, open)

  function handleOpenChange(next: boolean) {
    setOpen(next)
    if (!next) setQuery('')
  }

  function elegir(business: Business) {
    handleOpenChange(false)
    setEmpresa(business)
  }

  function irAAdhoc() {
    handleOpenChange(false)
    navigate('/admin/bateria/adhoc')
  }

  const empresas = data?.items ?? []

  return (
    <>
      <SearchComboboxShell
        title={t('admin.bateria.nuevaBateria.title')}
        open={open}
        onOpenChange={handleOpenChange}
        popoverClassName="w-80"
        align="end"
        trigger={
          <Button size="sm" data-testid="admin-bateria-nueva-cta">
            {t('admin.bateria.nuevaBateria.cta')}
          </Button>
        }>
        <Input
          maxLength={tope('BUSCA')}
          placeholder={t('admin.bateria.nuevaBateria.searchPlaceholder')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoComplete="off"
          autoFocus
          data-testid="nueva-bateria-search"
          className="h-12 shrink-0 rounded-none border-0 border-b border-border text-base focus-visible:ring-0 md:h-9 md:text-sm"
        />
        <ul className="flex-1 overflow-y-auto p-1 md:max-h-72 md:flex-none">
          <Seccion>{t('admin.bateria.nuevaBateria.seccionClientes')}</Seccion>
          {empresas.map((business) => (
            <li key={business.id}>
              <button
                type="button"
                data-testid={`nueva-bateria-empresa-${business.id}`}
                className="w-full rounded-md px-2 py-3 text-left text-sm break-words hover:bg-secondary md:py-1.5"
                onClick={() => elegir(business)}>
                <span className="block text-foreground">{business.name}</span>
                <span className="block truncate font-mono text-xs text-muted-foreground">{business.nit}</span>
              </button>
            </li>
          ))}
          {empresas.length === 0 && (
            <li className="px-2 py-1.5 text-sm text-muted-foreground" data-testid="nueva-bateria-sin-empresas">
              {t('admin.bateria.nuevaBateria.sinResultados')}
            </li>
          )}
          <Seccion>{t('admin.bateria.nuevaBateria.seccionAdhoc')}</Seccion>
          <li>
            <button
              type="button"
              data-testid="nueva-bateria-adhoc"
              className="w-full rounded-md px-2 py-3 text-left text-sm break-words hover:bg-secondary md:py-1.5"
              onClick={irAAdhoc}>
              {t('admin.bateria.nuevaBateria.adhocOpcion')}
            </button>
          </li>
        </ul>
      </SearchComboboxShell>

      {empresa && (
        <NuevaEvaluacionDialog
          businessId={empresa.id}
          open
          onOpenChange={(next) => !next && setEmpresa(null)}
          onCreated={(id) => navigate(`/admin/bateria/empresas/${empresa.id}/evaluaciones/${id}`)}
        />
      )}
    </>
  )
}
